import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useStore } from "zustand";
import * as Sentry from "@sentry/react";
import { IMS } from "@app/services/imsService";
import { actions } from "@app/utils";
import { createApiClient } from "@app/utils/apiClient";
import { store } from "../store";

const imsClient = createApiClient(actions["exd-storyteller-ai/ims"]);

/**
 * React hook for accessing IMS data and methods
 *
 * @returns {Object} IMS state and methods
 */
export function useIMS() {
  const [isSignedIn, setIsSignedIn] = useState(IMS.isSignedIn);
  const [userProfile, setUserProfile] = useState(IMS.userProfile);

  useEffect(() => {
    setIsSignedIn(IMS.isSignedIn);
    setUserProfile(IMS.userProfile);

    const interval = setInterval(() => {
      const newIsSignedIn = IMS.isSignedIn;
      const newUserProfile = IMS.userProfile;

      if (newIsSignedIn !== isSignedIn) {
        setIsSignedIn(newIsSignedIn);
      }
      if (newUserProfile !== userProfile) {
        setUserProfile(newUserProfile);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isSignedIn, userProfile]);

  return {
    isSignedIn,
    userProfile,
    signIn: () => IMS.signIn(),
    signOut: () => IMS.signOut(),
    signUp: () => IMS.signUp(),
    getAccessToken: () => IMS.getAccessToken(),
    getUserProfile: () => IMS.getUserProfile(),
  };
}

/**
 * React hook for accessing user profile from store
 *
 * @returns {Object|null} User profile or null
 */
export function useUserProfile() {
  const imsToken = useStore(store, (s) => s.ims?.token);

  const profileQuery = useQuery({
    queryKey: ["user", "profile", imsToken],
    enabled: !!imsToken,
    queryFn: async () => {
      const res = await imsClient.get("/profile");
      if (!res.ok) {
        throw new Error("Failed to load user profile");
      }
      const backend = res.data?.profile ?? {};
      const roles = Array.isArray(backend.roles) ? backend.roles : [];
      return {
        ...backend,
        roles,
        id: backend.userId ?? backend.id,
        fullName: backend.name ?? backend.fullName,
        firstName: backend.first_name ?? backend.firstName,
        lastName: backend.last_name ?? backend.lastName,
      };
    },
  });

  const [profile, setProfile] = useState(null);

  useEffect(() => {
    const imsState = store.getState().ims;
    if (imsState?.profile) {
      const imsProfile = { ...imsState.profile };
      imsProfile.ldap = imsProfile.email.split("@")[0];
      setProfile(imsProfile);

      Sentry.setUser({
        username: imsProfile.fullName,
        email: imsProfile.email,
        ownerOrg: imsProfile.ownerOrg,
      });
    }

    const unsubscribe = store.subscribe(
      (state) => state.ims?.profile,
      (newProfile) => {
        setProfile(newProfile);
      },
    );

    return unsubscribe;
  }, []);

  return profile ? { ...profile, ...profileQuery.data } : null;
}

/**
 * Resolve the current user's ldap (email prefix) from the store, waiting for
 * the IMS profile to load if it isn't ready yet.
 *
 * Reads live store state rather than a React snapshot, so it is safe to call
 * from async callbacks where a captured `userProfile` would be stale.
 *
 * @param {Object} [options]
 * @param {number} [options.timeoutMs=10000] Reject if the profile never loads.
 * @returns {Promise<string>} Resolves with the ldap once available.
 */
export function getUserLdap({ timeoutMs = 10000 } = {}) {
  const ldapFromState = () => {
    const email = store.getState().ims?.profile?.email;
    return email ? email.split("@")[0] : null;
  };

  const existing = ldapFromState();
  if (existing) {
    return Promise.resolve(existing);
  }

  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      unsubscribe();
      reject(new Error("Timed out waiting for user profile to load"));
    }, timeoutMs);

    const unsubscribe = store.subscribe(
      (state) => state.ims?.profile?.email,
      (email) => {
        if (email) {
          clearTimeout(timer);
          unsubscribe();
          resolve(email.split("@")[0]);
        }
      },
    );
  });
}

/**
 * React hook for accessing IMS token from store
 *
 * @returns {string|null} Access token or null
 */
export function useIMSToken() {
  const [token, setToken] = useState(null);

  useEffect(() => {
    const imsState = store.getState().ims;
    if (imsState?.token) {
      setToken(imsState.token);
    }

    const unsubscribe = store.subscribe(
      (state) => state.ims?.token,
      (newToken) => {
        setToken(newToken);
      },
    );

    return unsubscribe;
  }, []);

  return token;
}

/**
 * React hook for checking if user is logged in
 *
 * @returns {boolean} True if user is logged in
 */
export function useIsLoggedIn() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const imsState = store.getState().ims;
    setIsLoggedIn(!!imsState?.token && !!imsState?.profile);

    const unsubscribe = store.subscribe(
      (state) => state.isLoggedIn,
      (newIsLoggedIn) => {
        setIsLoggedIn(newIsLoggedIn);
      },
    );

    return unsubscribe;
  }, []);

  return isLoggedIn;
}
